import { TranslationKey } from './../../../../../../../../types/translation/TranslationKey';

import { Grid } from '@mui/material';
import React, { useContext, useMemo } from 'react';
import { testIds } from '../../../../../../../../util/identifiers/identifiers.util';
import { t } from '../../../../../../../../types/translation/Translator';
import { ProductMasterDataContext } from '../../../../../../../../context/ProductMasterDataContext';
import Selector from '../../../../../../Common/Selector';
import { ReorderUserRuleContext } from '../../../../../../../../context/ReorderUserRuleContext';
import Checkbox from '../../../../../../../../VentoryUI/components/common/Checkbox/Checkbox';
import ModalPane from '../../../../../../../../VentoryUI/components/common/Modal/components/ModalPane';
import { BackButtonTemplate } from '../../../../../../../../VentoryUI/components/common/Button/Templates/BackButton';
import { FinishButtonTemplate } from '../../../../../../../../VentoryUI/components/common/Button/Templates/FinishButton';
import { CompanyContext } from '../../../../../../../../context/CompanyContext';

interface ReorderUserRuleProductPaneProps {
  next: () => void;
  back: () => void;
  existingRuleIds: Set<string>;
  stockLocationIds: string[];
  pmdIds: string[];
  setPmdIds: (pmdIds: string[]) => void;
  appliesToAllLocations: boolean;
  appliesToAllProducts: boolean;
  setAppliesToAllProducts: (applies: boolean) => void;
}

export default function ReorderUserRuleProductPane({
  next,
  back,
  existingRuleIds,
  pmdIds,
  setPmdIds,
  stockLocationIds,
  appliesToAllLocations,
  appliesToAllProducts,
  setAppliesToAllProducts,
}: ReorderUserRuleProductPaneProps) {
  const { currentCompany } = useContext(CompanyContext);
  const { productMasterData } = useContext(ProductMasterDataContext);

  const { reorderUserRules } = useContext(ReorderUserRuleContext);

  const disallowedProducts = useMemo(() => {
    if (appliesToAllLocations || !stockLocationIds.length) stockLocationIds = [''];
    const slSet = new Set(stockLocationIds);

    const pmdIds = [];
    for (const rule of reorderUserRules.values()) {
      if (existingRuleIds.has(rule.id)) continue;
      if (slSet.has(rule.stockLocationId || '')) {
        pmdIds.push(rule.productMasterDataId);
      }
    }

    return new Set(pmdIds);
  }, []);

  return (
    <ModalPane
      overflow='auto'
      footerButtons={[
        BackButtonTemplate(back),
        FinishButtonTemplate(next, { disabled: !appliesToAllProducts && !pmdIds.length }),
      ]}
    >
      <Grid container height={'100%'}>
        <Grid item xs={12}>
          <Checkbox
            label={t(TranslationKey.appliesToAllProducts).singular.upper}
            value={appliesToAllProducts}
            onChange={value => setAppliesToAllProducts(value)}
          />
        </Grid>
        {!appliesToAllProducts ? (
          <Grid item xs={12}>
            <Selector
              testId={testIds.products}
              placeholder={t(TranslationKey.ruleProductText).singular.label}
              values={[...productMasterData.values()]
                .filter(pmd => pmd.companyId === currentCompany.id)
                .map(pmd => pmd.id)}
              checkedValues={pmdIds}
              toText={item => productMasterData.get(item)?.productName || 'Unknown Product'}
              onChange={checked => setPmdIds(checked)}
              disabled={item => disallowedProducts.has(item)}
              toElement={item => {
                const pmd = productMasterData.get(item);

                return (
                  <Grid container columnSpacing={1}>
                    <Grid item xs={12}>
                      <p className='text-normal font-normal'>
                        ${pmd?.productName || t(TranslationKey.unknownProduct).singular.label}
                      </p>
                    </Grid>
                    {pmd ? (
                      <Grid item xs={12}>
                        <p className='text-sm text-gray-400'>{`${pmd.productNumber}`}</p>
                      </Grid>
                    ) : null}
                  </Grid>
                );
              }}
            />
          </Grid>
        ) : null}
      </Grid>
    </ModalPane>
  );
}
